import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { topUpRequest, topUpSummary } from "../../../services/transactionService";
import { errorToast } from "../../Toasts/Toasts";
import "react-phone-number-input/style.css";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import { Link } from "react-router-dom";
import CustomAlert from "../../Shared/CustomAlert";
import { mapPhoneNumberToCarrier, getAsaanCarrier } from "../../../utils/carrierUtils";

import { useDebouncedApiCall } from "../../../hooks/useDebouncedApiCall";

const TopUpThroughRetailer = ({ masterData, setShowProductSelect, setProductAmount, productAmount, finalCommission, productId, handleReRenderData, setShowImage, showProductLink, setProductFlag, convertedCurrency, carrierType, setCarrierType, mode = "mobile", colClass = "col-md-5" }) => {
  const [topUpAmountSummary, setTopUpAmountSummary] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [loader, setLoader] = useState(false);
  const [showCustomeAlertModal, setShowCustomeAlertModal] = useState(false);
  const keywordRef = useRef("");
  const [alertMessage, setAlertMessage] = useState("");
  const [showClassName, setShowClassName] = useState("");
  const [iconClassName, setIconClassName] = useState("");
  const [carrierError, setCarrierError] = useState("");
  const [isTopUpFailed, setIsTopUpFailed] = useState(false);
  const [invalidateSummary, setInvalidateSummary] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const { execute: debouncedFetch, cancel: cancelDebouncedFetch } = useDebouncedApiCall(600);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      mobileNumber: "",
      carrier: "",
      topUpAmount: productAmount ? productAmount : "",
      requestersName: "",
      aggregatorId: "",
    },
  });

  const fetchData = async (isInvalidating) => {
    try {
      setIsSummaryLoading(true);
      const amount = isInvalidating ? parseFloat(getValues("topUpAmount")) : parseFloat(productAmount);
      const response = await topUpSummary({ amount, final_commission: finalCommission, product_id: productId });
      setTopUpAmountSummary(response);
    } catch (error) {
      setTopUpAmountSummary(null);
      errorToast(error?.response?.data?.message || error?.response?.data?.errors?.[0]?.message || "Something went wrong");
    } finally {
      setInvalidateSummary(false);
      setLoader(false);
      setIsSummaryLoading(false);
    }
  };

  useEffect(() => {
    // Update the topUpAmount input field whenever productAmount changes
    setValue("topUpAmount", productAmount);
    if (productAmount) {
      fetchData();
    }
  }, [productAmount]);

  useEffect(() => {
    if (invalidateSummary) {
      fetchData(true);
    }
  }, [invalidateSummary]);

  const applyCarrierToForm = (carrierObj) => {
    const selectedCarrier = masterData?.carriers?.find((c) => c?.id === carrierObj?.id);
    if (!selectedCarrier) return false;
  
    setValue("carrier", { label: selectedCarrier.name, value: selectedCarrier.id });
    setValue("aggregatorId", selectedCarrier.aggregatorId);
    setCarrierError("");
    return true;
  };

  useEffect(() => {
    if (carrierType === "asaan") {
      applyCarrierToForm(getAsaanCarrier());
      return;
    }
  
    const carrierObj = mapPhoneNumberToCarrier(phoneCode, phoneNumber);
    if (carrierObj) {
      applyCarrierToForm(carrierObj);
    } else if (phoneNumber.length) {
      setValue("carrier", "");
      setCarrierError("We do not serve this region");
    }
  }, [phoneNumber, carrierType, phoneCode]);

  const carrierOptions = () => {
    return masterData?.carriers?.map((data) => ({
      label: data?.name,
      value: data?.id,
    }));
  };

  const onSubmit = async (formData) => {
    try {
      let obj = {
        phone_code: phoneCode,
        mobile_number: phoneNumber,
        requested_by: formData?.requestersName,
        aggregator_id: formData.aggregatorId || null,
        carrier_id: formData?.carrier?.value,
        original_currency_type: topUpAmountSummary?.sourceCurrency,
        converted_currency_type: topUpAmountSummary?.convertedCurrency,
        commission: topUpAmountSummary?.commissionAmountOnConvertedCurrency,
        service_fee: topUpAmountSummary?.serviceFee,
        vat: topUpAmountSummary?.vat,
        total_amount_source_currency: topUpAmountSummary?.totalSourceAmount,
        total_amount_converted_currency: topUpAmountSummary?.totalConvertedAmount,
        topup_amount_source_currency: topUpAmountSummary?.sourceAmount,
        topup_amount_converted_currency: topUpAmountSummary?.convertedAmount,
      };

      console.log("🚀 -------------------------------🚀")
      console.log("🚀 ~ :128 ~ onSubmit ~ obj:", obj)
      console.log("🚀 -------------------------------🚀")

      setLoader(true);
      const response = await topUpRequest(obj);
      if (response?.success) {
        if (response?.message?.toLowerCase()?.includes("success")) {
          setShowClassName("text-success check info");
          setIconClassName("ti ti-check fs-1");
          handleReset();
          handleReRenderData(response);
          setShowProductSelect(false);
          setShowImage(true);
          setIsTopUpFailed(false);
        } else if (response?.message?.toLowerCase()?.includes("process")) {
          setShowClassName("text-warning check warning");
          setIconClassName("ti ti-loader fs-1");
          handleReset();
          handleReRenderData(response);
          setShowProductSelect(false);
          setShowImage(true);
          setIsTopUpFailed(false);
        } else {
          setShowClassName("text-danger check danger");
          setIconClassName("ti ti-x fs-1");
          setIsTopUpFailed(true);
        }
        setAlertMessage(response?.message);
        setShowCustomeAlertModal(true);
      } else {
        errorToast(response?.message);
        setIsTopUpFailed(false);
      }
    } catch (error) {
      errorToast(error?.response?.data?.message);
      setIsTopUpFailed(false);
    } finally {
      setLoader(false);
    }
  };

  const handleTopUpAmountChange = async (event) => {
    setShowProductSelect(false);
    setShowImage(true);
    const keyword = event?.target?.value?.trim();

    if (keyword !== "" && keyword.length >= 1) {
      setLoader(true);
      setIsSummaryLoading(true);
      keywordRef.current = keyword; // Update the ref with the latest keyword

      debouncedFetch(async (signal) => {
        if (keywordRef.current === keyword) {
          try {
            const response = await topUpSummary({ amount: parseFloat(keyword) }, { signal });
            setTopUpAmountSummary(response);
            setLoader(false);
            setIsSummaryLoading(false);
          } catch (error) {
            if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") return;
            setTopUpAmountSummary(null);
            setLoader(false);
            setIsSummaryLoading(false);
            errorToast(error?.response?.data?.message || error?.response?.data?.errors?.[0]?.message || "Something went wrong");
          }
        }
      });
    } else {
      cancelDebouncedFetch();
      keywordRef.current = "";
      setTopUpAmountSummary(null);
      setIsSummaryLoading(false);
      setLoader(false);
    }
  };

  const handleReset = () => {
    reset({
      mobileNumber: "",
      requestersName: "",
      topUpAmount: "",
      carrier: "",
    });
    setTopUpAmountSummary(null);
    setShowProductSelect(false);
    setShowImage(true);
    setCarrierError("");
    setIsTopUpFailed(false);
    if (mode === "asaan") {
      applyCarrierToForm(getAsaanCarrier());
    } else {
      setCarrierType("allCarriers");
    }
  };

  const handleChange = (value) => {
    if (value === "+93" || value === "+977" || value === "+90") {
      setProductFlag(value);
    }
    const stringValue = String(value);
    const parsedPhoneNumber = parsePhoneNumber(stringValue);
    if (parsedPhoneNumber) {
      setPhoneCode(`+${parsedPhoneNumber?.countryCallingCode}`);
      setPhoneNumber(parsedPhoneNumber?.nationalNumber);
      setValue("mobileNumber", stringValue);
    } else {
      setPhoneCode("");
      setPhoneNumber("");
      setValue("mobileNumber", ""); // Ensure the field value is updated
    }
    if (errors.mobileNumber) {
      setValue("mobileNumber", value, { shouldValidate: true });
    }
  };

  const handleProductSelection = () => {
    if (carrierType === "asaan") return;
    setShowProductSelect(true);
    setShowImage(false);
  };

  const handleCarrierChange = (selectedOption) => {
    setValue("carrier", selectedOption);
    const carrierName = selectedOption?.label;

    if (carrierName !== "e& Afghanistan" && carrierName !== "Roshan") {
      setCarrierError("This carrier's top-up feature is coming soon...");
    } else {
      setCarrierError("");
    }
  };

  return (
    <>
      <div className={colClass}>
        <div className="card mt-3 mt-lg-0">
          <div className="card-header border-bottom mb-2">
            <div className="card-title mb-0">
              <h5 className="mb-0">{mode === "asaan" ? "Asaan Top Up" : "Top Up"}</h5>
            </div>
          </div>
          <div className="card-body p-4">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                <div className="col-lg-6 mb-3">
                  <label htmlFor="phone" className="required form-label">
                    Mobile Number
                  </label>
                  <Controller
                    name="mobileNumber"
                    control={control}
                    rules={{
                      required: "Required",
                      minLength: {
                        value: 7,
                        message: "Mobile number must be at least 7 digits long. ",
                      },
                      maxLength: {
                        value: 16,
                        message: "Mobile number must be at most 15 digits long. ",
                      },
                      validate: (value) => {
                        const phoneNumber = value.replace(/[^0-9]/g, "");
                        return (phoneNumber.length >= 7 && phoneNumber.length <= 15) || "Mobile number must be between 7 and 15 digits. ";
                      },
                    }}
                    render={({ field }) => (
                      <>
                        <PhoneInput
                          key={carrierType}
                          className="form-control"
                          international
                          countryCallingCodeEditable={false}
                          defaultCountry="AF"
                          value={field.value}
                          {...(carrierType !== "asaan" && { countries: ["AF", "TR", "NP"] })}
                          onChange={(value) => {
                            handleChange(value);
                          }}
                        />
                        {errors?.mobileNumber && <small className="text-danger">{errors.mobileNumber.message}</small>}
                        {carrierError && <span className="text-danger">{carrierError}</span>}
                      </>
                    )}
                  />
                </div>
                <div className="col-lg-6 mb-3" style={{ display: "none" }}>
                  <label className="form-label required">Carrier</label>
                  <Controller
                    name="carrier"
                    control={control}
                    rules={{ required: "Required " }}
                    render={({ field }) => (
                      <>
                        <Select {...field} options={carrierOptions()} isSearchable placeholder="Select Carrier..." onChange={(selectedOption) => handleCarrierChange(selectedOption)} />
                        {errors.carrier && <span className="text-danger">{errors.carrier.message}</span>}
                        {carrierError && <span className="text-danger">{carrierError}</span>}
                      </>
                    )}
                  />
                </div>
                <div className="col-lg-6 mb-3">
                  <label className="form-label required">
                    Amount (Retailer's Country)
                    {/* {convertedCurrency?.retailerConversion?.source_currency_code && <small>({convertedCurrency.retailerConversion.source_currency_code})</small>} */}
                  </label>
                  <Controller
                    name="topUpAmount"
                    control={control}
                    // rules={{
                    //   required: "Required",
                    //   min: { value: 40, message: "Amount must be greater or equal to 40" },
                    //   max: { value: 4000, message: "Amount must be less than or equal to 4000" },
                    // }}
                    render={({ field }) => (
                      <>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="form-control mb-3"
                          {...field}
                          placeholder="90"
                          onChange={(e) => {
                            const safeValue = e.target.value.replace(/[^0-9.]/g, "");
                            e.target.value = safeValue;
                            field.onChange(e);
                            handleTopUpAmountChange(e);
                          }}
                          min={0}
                        />
                        {errors.topUpAmount && <span className="text-danger">{errors.topUpAmount.message}</span>}
                      </>
                    )}
                  />
                </div>
                <div className="col-lg-6 mb-3">
                  <label className="form-label required">Requester's Name</label>
                  <Controller
                    name="requestersName"
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field }) => (
                      <>
                        <input type="text" className="form-control mb-3" {...field} placeholder="Akira" />
                        {errors.requestersName && <span className="text-danger">{errors.requestersName.message}</span>}
                      </>
                    )}
                  />
                </div>
                {showProductLink && carrierType !== "asaan" && (
                  <div className="col-lg-12 mb-3">
                    <Link to="#" className="select-plan" onClick={handleProductSelection}>
                      Select From Products <i className="ti ti-arrow-narrow-right ms-1"></i>
                    </Link>
                  </div>
                )}
              </div>
              <div className="border-bottom border-bottom-dashed mt-0 mb-4"></div>
              {topUpAmountSummary && !isSummaryLoading && (
                <>
                  <div className="row ">
                    <div className="col-md-12 mb-md-0 mb-3">
                      <div className="d-flex justify-content-between mb-0">
                        <h5 className="mb-0">Summary</h5>
                      </div>
                    </div>
                  </div>
                  <div className="row justify-content-center">
                    <div className="col-md-12">
                      {/* Summary Section */}
                      <div className="d-flex w-100 flex-wrap justify-content-between py-2 mt-3">
                        <div className="me-2">
                          <h6 className="mb-0">Amount</h6>
                        </div>
                        <div className="user-progress text-right align-items-center gap-1">
                          <p className="mb-0 fw-medium h5">
                            <span className="text-primary me-2">
                              ({(topUpAmountSummary?.sourceAmount).toFixed(2)} <small>{topUpAmountSummary?.sourceCurrency}</small>)
                            </span>
                            <span className="h5">
                              {(topUpAmountSummary?.convertedAmount).toFixed(2)} <small>{topUpAmountSummary?.convertedCurrency}</small>
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="d-flex w-100 flex-wrap align-items-center justify-content-between py-2">
                        <div className="me-2">
                          <h6 className="mb-0">Service Fee</h6>
                        </div>
                        <div className="user-progress d-flex align-items-center gap-1">
                          <p className="mb-0 fw-medium h6">
                            {(topUpAmountSummary?.serviceFee + (topUpAmountSummary.commissionAmountOnConvertedCurrency ? topUpAmountSummary?.commissionAmountOnConvertedCurrency : 0)).toFixed(2)} <small>{topUpAmountSummary?.convertedCurrency}</small>
                          </p>
                        </div>
                      </div>
                      <div className="d-flex w-100 flex-wrap align-items-center justify-content-between py-2">
                        <div className="me-2">
                          <h6 className="mb-0">Taxes</h6>
                        </div>
                        <div className="user-progress d-flex align-items-center gap-1">
                          <p className="mb-0 fw-medium h6">
                            {(topUpAmountSummary?.vat).toFixed(2)} <small>{topUpAmountSummary?.convertedCurrency}</small>
                          </p>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between border-top mt-2 pt-2">
                        <h5 className="mb-1">Total</h5>
                        <div>
                          <p className=" h4 mb-1">
                            <span className="text-primary me-2 fs-5">
                              ({(topUpAmountSummary?.totalSourceAmount).toFixed(2)} <small>{topUpAmountSummary?.sourceCurrency}</small>)
                            </span>
                            {(topUpAmountSummary?.totalConvertedAmount).toFixed(2)} <small>{topUpAmountSummary?.convertedCurrency}</small>
                          </p>
                        </div>
                      </div>
                      {topUpAmountSummary?.commissionAmountOnOriginalCurrency ? (
                        <div className="d-flex justify-content-between mt-0 pt-0 border-top pt-2">
                          <h6>Total Commission Earned</h6>
                          <div>
                            <p className="h5">
                              <span className="text-blue me-2 fs-5">
                                ({(topUpAmountSummary?.commissionAmountOnOriginalCurrency).toFixed(2)} <small>{topUpAmountSummary?.sourceCurrency}</small>)
                              </span>
                              {(topUpAmountSummary?.commissionAmountOnConvertedCurrency).toFixed(2)} <small>{topUpAmountSummary?.convertedCurrency}</small>
                            </p>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <span className="text-primary"> * Government taxes may apply on final top-up amount</span>
                  </div>
                </>
              )}
              {(loader || isSummaryLoading) && (
                <div className="text-center">
                  <span className="spinner-border h-15px w-15px align-middle text-danger" />
                </div>
              )}
              <div className="col-12 text-center mt-4">
                <button type="submit" className="btn btn-primary rounded-pill me-sm-3 me-1 waves-effect waves-light" data-bs-target="#Success" disabled={Boolean(carrierError) || loader}>
                  Top-Up
                </button>
                <button type="reset" className="btn btn-label-secondary rounded-pill btn-reset waves-effect" onClick={handleReset} disabled={loader}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <CustomAlert showCustomeAlertModal={showCustomeAlertModal} setShowCustomeAlertModal={setShowCustomeAlertModal} text={alertMessage} showClassName={showClassName} iconClassName={iconClassName} isTopUpFailed={isTopUpFailed} setInvalidateSummary={setInvalidateSummary} handleReset={handleReset} />
    </>
  );
};

export default TopUpThroughRetailer;
